import { call, put, takeLatest } from 'redux-saga/effects';

import {
  FETCH_FRAML_CUSTOMER_AGGREGATE,
  fetchFramlCustomerAggregate,
  fetchFramlCustomerAggregateSuccess,
  fetchFramlCustomerAggregateFail,
  RESOLVE_FRAML_ALERT,
  resolveFramlAlertSuccess,
  resolveFramlAlertFail,
  fetchFramlCustomerAlerts,
  FETCH_FRAML_CUSTOMER_SCORE_HISTORY,
  fetchFramlCustomerScoreHistorySuccess,
  fetchFramlCustomerScoreHistoryFail,
  FETCH_FRAML_CUSTOMER_ALERTS,
  fetchFramlCustomerAlertsSuccess,
  fetchFramlCustomerAlertsFail,
  FETCH_FRAML_CUSTOMER_RISK_FACTORS,
  fetchFramlCustomerRiskFactorsSuccess,
  fetchFramlCustomerRiskFactorsFail,
  FETCH_FRAML_CUSTOMER_DETAILS,
  fetchFramlCustomerDetailsSuccess,
  fetchFramlCustomerDetailsFail,
  FETCH_FRAML_ASSOCIATED_CUSTOMERS,
  fetchFramlAssociatedCustomersSuccess,
  fetchFramlAssociatedCustomersFail,
  FETCH_FRAML_ASSOCIATED_ACCOUNTS,
  fetchFramlAssociatedAccountsSuccess,
  fetchFramlAssociatedAccountsFail,
  FETCH_FRAML_SIMILAR_KYC,
  fetchFramlSimilarKycSuccess,
  fetchFramlSimilarKycFail
} from '../../redux/actions/cm/framlCustomerDetail';

import {
  fetchAggregate,
  fetchScoreHistory,
  fetchAlerts,
  fetchRiskFactors,
  fetchCustomerDetails,
  fetchAssociatedCustomers,
  fetchAssociatedAccounts,
  fetchSimilarKyc
} from '../../services/cm/framlCustomerDetail';
import {
  assignAlertsToUser,
  changeAlertStatus
} from '../../services/cm/alerts';

// Each watcher uses takeLatest so a fast customer→customer navigation (e.g.
// eye-icon A → B) cancels the in-flight fetch for A before its stale response
// can be put into the FLAT (non-keyed) reducer slice and overwrite B's data.
// The worker generator is handed directly to takeLatest — no inner fork, which
// would detach the worker into an untracked task and defeat the cancellation.

// --- Aggregate ---
function* fetchFramlCustomerAggregateWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchAggregate, customerKey);
    yield put(fetchFramlCustomerAggregateSuccess(data));
  } catch (error) {
    yield put(fetchFramlCustomerAggregateFail(error.message));
  }
}

export function* watchFetchFramlCustomerAggregate() {
  yield takeLatest(
    FETCH_FRAML_CUSTOMER_AGGREGATE,
    fetchFramlCustomerAggregateWorker
  );
}

// --- Score History ---
function* fetchFramlCustomerScoreHistoryWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchScoreHistory, customerKey);
    yield put(fetchFramlCustomerScoreHistorySuccess(data));
  } catch (error) {
    yield put(fetchFramlCustomerScoreHistoryFail(error.message));
  }
}

export function* watchFetchFramlCustomerScoreHistory() {
  yield takeLatest(
    FETCH_FRAML_CUSTOMER_SCORE_HISTORY,
    fetchFramlCustomerScoreHistoryWorker
  );
}

// --- Alerts ---
function* fetchFramlCustomerAlertsWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchAlerts, customerKey);
    yield put(fetchFramlCustomerAlertsSuccess(data));
  } catch (error) {
    yield put(fetchFramlCustomerAlertsFail(error.message));
  }
}

export function* watchFetchFramlCustomerAlerts() {
  yield takeLatest(FETCH_FRAML_CUSTOMER_ALERTS, fetchFramlCustomerAlertsWorker);
}

// --- Risk Factors ---
function* fetchFramlCustomerRiskFactorsWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchRiskFactors, customerKey);
    yield put(fetchFramlCustomerRiskFactorsSuccess(data));
  } catch (error) {
    yield put(fetchFramlCustomerRiskFactorsFail(error.message));
  }
}

export function* watchFetchFramlCustomerRiskFactors() {
  yield takeLatest(
    FETCH_FRAML_CUSTOMER_RISK_FACTORS,
    fetchFramlCustomerRiskFactorsWorker
  );
}

// --- Customer Details (party identity) ---
function* fetchFramlCustomerDetailsWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchCustomerDetails, customerKey);
    yield put(fetchFramlCustomerDetailsSuccess(data));
  } catch (error) {
    yield put(fetchFramlCustomerDetailsFail(error.message));
  }
}

export function* watchFetchFramlCustomerDetails() {
  yield takeLatest(
    FETCH_FRAML_CUSTOMER_DETAILS,
    fetchFramlCustomerDetailsWorker
  );
}

// --- Associated Customers ---
function* fetchFramlAssociatedCustomersWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchAssociatedCustomers, customerKey);
    yield put(fetchFramlAssociatedCustomersSuccess(data));
  } catch (error) {
    yield put(fetchFramlAssociatedCustomersFail(error.message));
  }
}

export function* watchFetchFramlAssociatedCustomers() {
  yield takeLatest(
    FETCH_FRAML_ASSOCIATED_CUSTOMERS,
    fetchFramlAssociatedCustomersWorker
  );
}

// --- Associated Accounts ---
function* fetchFramlAssociatedAccountsWorker({ payload: customerKey }) {
  try {
    const data = yield call(fetchAssociatedAccounts, customerKey);
    yield put(fetchFramlAssociatedAccountsSuccess(data));
  } catch (error) {
    yield put(fetchFramlAssociatedAccountsFail(error.message));
  }
}

export function* watchFetchFramlAssociatedAccounts() {
  yield takeLatest(
    FETCH_FRAML_ASSOCIATED_ACCOUNTS,
    fetchFramlAssociatedAccountsWorker
  );
}

// --- Similar KYC ---
// FIN-12966 — forwards { limit, offset } so Similar KYC pages server-side. takeLatest already
// discards a superseded page request, so rapid page clicks resolve to the last one.
function* fetchFramlSimilarKycWorker({ payload: customerKey, paging }) {
  try {
    const data = yield call(fetchSimilarKyc, customerKey, paging);
    yield put(fetchFramlSimilarKycSuccess(data));
  } catch (error) {
    yield put(fetchFramlSimilarKycFail(error.message));
  }
}

export function* watchFetchFramlSimilarKyc() {
  yield takeLatest(FETCH_FRAML_SIMILAR_KYC, fetchFramlSimilarKycWorker);
}

// --- FIN-12885: per-alert Resolve = auto-assign(acting user) -> NO_ISSUE -> refetch ---
// The strict status-transition table has no UNASSIGNED -> NO_ISSUE edge (kept strict by
// product decision), so resolving an unassigned alert 404'd with a swallowed error and
// the UI looked dead. Assign first, then transition, then refetch so panels update.
export function* resolveFramlAlertWorker({
  payload: { customerKey, alertId, userId }
}) {
  try {
    yield call(assignAlertsToUser, { alertIds: [alertId], userId });
    yield call(changeAlertStatus, { alertId, status: 'NO_ISSUE' });
    yield put(resolveFramlAlertSuccess({ alertId }));
    yield put(fetchFramlCustomerAggregate(customerKey));
    yield put(fetchFramlCustomerAlerts(customerKey));
  } catch (error) {
    yield put(resolveFramlAlertFail({ message: error.message }));
  }
}

export function* watchResolveFramlAlert() {
  yield takeLatest(RESOLVE_FRAML_ALERT, resolveFramlAlertWorker);
}
